import {
  ColorVisionAnswerValue,
  colorVisionSubtypeInferenceMap,
  colorVisionValues,
  availableColorVisionTestConfigurationOptions,
  OPTION_KEY_ANSWER_MAP,
} from "@constants/testConstants/testSpecificConstants/colorVisionConstants";
import { VIRTUAL_ASSISTANT_LANGUAGE_LIST } from "@constants/languageConstants";
import {
  AvailablePreTestConfigurationOptions,
  ColorVisionCollectedData,
  ColorVisionPlateResult,
  TestConfiguration,
  TestLanguage,
  TestType,
} from "types/testTypes";
import { ColorVisionPlate } from "types/tests/specifics";
import colorVisionTranslations from "@data/color_vision_translations.json";
import { cloneDeep } from "lodash";

function normalizeToken(input: string): string {
  return input.trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
}

const patientStrings: Record<string, Record<string, string>> = colorVisionTranslations;

const localizedAnswers: Record<string, ColorVisionAnswerValue> = Object.fromEntries(
  Object.values(patientStrings).flatMap((strings) =>
    Object.entries(strings)
      .filter(([key]) => key in OPTION_KEY_ANSWER_MAP)
      .map(([key, label]) => [normalizeToken(label), OPTION_KEY_ANSWER_MAP[key]]),
  ),
);

export function normalizeColorVisionAnswer(answer: unknown): ColorVisionAnswerValue {
  if (typeof answer === "number") return answer;
  if (answer == null) return "nothing";

  const normalized = normalizeToken(String(answer));

  if (/^\d+$/.test(normalized)) {
    return Number(normalized);
  }

  if (localizedAnswers[normalized] !== undefined) {
    return localizedAnswers[normalized];
  }

  if (normalized === "none" || normalized === "nothing" || normalized === "no_number") {
    return "nothing";
  }
  if (normalized === "no_line" || normalized === "no_lines" || normalized === "no_trace") {
    return "no_lines";
  }
  if (normalized === "1_line" || normalized === "one_line" || normalized === "single_line") {
    return "1_line";
  }
  if (normalized === "2_lines" || normalized === "two_lines" || normalized === "both_lines") {
    return "2_lines";
  }
  if (normalized === "trace" || normalized === "trace_line" || normalized === "line") {
    return "trace_line";
  }
  if (
    normalized === "line_1" ||
    normalized === "first_line" ||
    normalized === "upper_line" ||
    normalized === "left_line"
  ) {
    return "line_1";
  }
  if (
    normalized === "line_2" ||
    normalized === "second_line" ||
    normalized === "lower_line" ||
    normalized === "right_line"
  ) {
    return "line_2";
  }
  return normalized;
}

const ANSWER_ALIASES: Record<string, ColorVisionAnswerValue[]> = {
  no_lines: ["nothing"],
  "1_line": ["trace_line"],
};

export function isColorVisionAnswerCorrect(
  plate: number,
  normalizedResponse: ColorVisionAnswerValue,
): boolean {
  const expected = colorVisionValues[plate] as ColorVisionAnswerValue;

  if (expected === normalizedResponse) return true;

  return (ANSWER_ALIASES[String(expected)] ?? []).includes(normalizedResponse);
}

export function buildColorVisionPlateResult(
  plate: number,
  rawAnswer: string | number,
  responseTimeMs?: number,
  headsetIsCorrect?: unknown,
): ColorVisionPlateResult {
  const normalizedResponse = normalizeColorVisionAnswer(rawAnswer);
  const expected = colorVisionValues[plate] as string | number;

  return {
    plate,
    response: normalizedResponse,
    isCorrect:
      typeof headsetIsCorrect === "boolean"
        ? headsetIsCorrect
        : isColorVisionAnswerCorrect(plate, normalizedResponse),
    expected,
    responseTimeMs,
  };
}

export function isColorVisionPlateResult(item: any): item is ColorVisionPlateResult {
  return (
    item &&
    typeof item === "object" &&
    typeof item.plate === "number" &&
    "response" in item &&
    "isCorrect" in item &&
    typeof item.isCorrect === "boolean"
  );
}

export type ColorVisionDetailedResult = {
  isCorrect: boolean | null;
  response?: string | number;
};

export function toColorVisionDetailedResults(
  results: boolean[] | ColorVisionPlateResult[] | null | undefined,
  totalPlateCount?: number,
): ColorVisionDetailedResult[] {
  const resolveTargetCount = (fallback = 0): number => {
    if (typeof totalPlateCount === "number" && Number.isFinite(totalPlateCount)) {
      return Math.max(0, Math.floor(totalPlateCount));
    }
    return fallback;
  };

  if (!Array.isArray(results)) {
    return Array.from({ length: resolveTargetCount() }, () => ({ isCorrect: null }));
  }

  if (results.length === 0) {
    return Array.from({ length: resolveTargetCount() }, () => ({ isCorrect: null }));
  }

  if (typeof results[0] === "boolean" || results[0] === null) {
    const legacy = results as Array<boolean | null>;
    const inferredCount =
      legacy.reduce((lastAnsweredIndex, value, index) => {
        return typeof value === "boolean" ? index : lastAnsweredIndex;
      }, -1) + 1;
    const targetCount = resolveTargetCount(inferredCount);

    return Array.from({ length: targetCount }, (_, i) => ({
      isCorrect: typeof legacy[i] === "boolean" ? legacy[i] : null,
    }));
  }

  const structured = results as ColorVisionPlateResult[];
  const byPlate = new Map<number, ColorVisionPlateResult>();
  let maxPlate = 0;
  structured.forEach((item, index) => {
    if (isColorVisionPlateResult(item)) {
      byPlate.set(item.plate, item);
      if (item.plate > maxPlate) {
        maxPlate = item.plate;
      }
    } else {
      byPlate.set(index + 1, {
        plate: index + 1,
        response: "",
        isCorrect: false,
      });
      if (index + 1 > maxPlate) {
        maxPlate = index + 1;
      }
    }
  });

  const targetCount = resolveTargetCount(maxPlate);
  return Array.from({ length: targetCount }, (_, i) => {
    const item = byPlate.get(i + 1);
    return item ? { isCorrect: item.isCorrect, response: item.response } : { isCorrect: null };
  });
}

export function toColorVisionCorrectnessArray(
  results: boolean[] | ColorVisionPlateResult[] | null | undefined,
  totalPlateCount?: number,
): Array<boolean | null> {
  return toColorVisionDetailedResults(results, totalPlateCount).map((item) => item.isCorrect);
}

export function buildColorVisionProgressStateFromCollectedData(
  collectedData: ColorVisionCollectedData | null | undefined,
  fallbackPlateCount: number,
): {
  results: Array<boolean | null>;
  index: number;
} {
  // A 0 plateCount (operator-controlled Ishihara reports no fixed count) must
  // fall back to the caller's count; otherwise plateCount collapses to 0 and
  // the resumed index is clamped to 0.
  const plateCount = Math.max(
    0,
    Math.floor(collectedData?.initData?.plateCount || fallbackPlateCount),
  );
  const results = toColorVisionCorrectnessArray(collectedData?.results, plateCount);
  const answeredCount = results.filter((value) => typeof value === "boolean").length;
  const currentIndex =
    typeof collectedData?.currentIndex === "number" && Number.isFinite(collectedData.currentIndex)
      ? Math.max(0, Math.min(Math.floor(collectedData.currentIndex), plateCount))
      : null;

  return {
    results,
    index: currentIndex ?? Math.min(answeredCount, plateCount),
  };
}

export type InferredColorVisionSubtype = "protan" | "deutan" | "unclassified" | "insufficient_data";

export function inferColorVisionSubtype(
  results: boolean[] | ColorVisionPlateResult[] | null | undefined,
): InferredColorVisionSubtype {
  if (!Array.isArray(results) || results.length === 0) {
    return "insufficient_data";
  }
  if (typeof results[0] === "boolean") {
    return "insufficient_data";
  }

  const structured = results as ColorVisionPlateResult[];
  const byPlate = new Map<number, ColorVisionAnswerValue>();
  structured.forEach((item, index) => {
    const plate = typeof item?.plate === "number" ? item.plate : index + 1;
    byPlate.set(plate, normalizeColorVisionAnswer(item?.response));
  });

  let protanVotes = 0;
  let deutanVotes = 0;
  let compared = 0;

  Object.entries(colorVisionSubtypeInferenceMap).forEach(([plateKey, rule]) => {
    const plate = Number(plateKey);
    const observed = byPlate.get(plate);
    if (observed === undefined) return;
    if (observed === rule.protanAnswer) {
      protanVotes += 1;
      compared += 1;
      return;
    }
    if (observed === rule.deutanAnswer) {
      deutanVotes += 1;
      compared += 1;
    }
  });

  if (compared === 0) return "insufficient_data";
  if (protanVotes === deutanVotes) return "unclassified";
  return protanVotes > deutanVotes ? "protan" : "deutan";
}

export function translateColorVisionPlates(
  plates: ColorVisionPlate[],
  language?: TestLanguage | null,
): ColorVisionPlate[] {
  const strings = patientStrings[language ?? ""] ?? patientStrings[TestLanguage.English];

  const translate = (value: string): string => strings[value] ?? value;
  const translateIfKey = (value: string | number): string | number =>
    typeof value === "string" ? translate(value) : value;

  return plates.map((plate) => ({
    ...plate,
    description: translate(plate.description),
    correct: translateIfKey(plate.correct),
    options: plate.options.map(translateIfKey),
  }));
}

export function GetUpdatedAvailableIshiharaConfigsAfterConfigUpdate(
  requestedUpdate: Partial<TestConfiguration>,
  baseOptions?: AvailablePreTestConfigurationOptions | null,
): AvailablePreTestConfigurationOptions {
  let availableCVConfig: AvailablePreTestConfigurationOptions = cloneDeep(
    baseOptions ?? availableColorVisionTestConfigurationOptions,
  );

  const testType: TestType | undefined = requestedUpdate.testType;

  if (testType === TestType.ColorVision) {
    availableCVConfig = {
      ...availableCVConfig,
      languageOptions: null,
      testSpecificConfigurationOptions: {
        ...availableCVConfig.testSpecificConfigurationOptions,
        plateNumberOptions: null,
        toggleShowTutorial: null,
      },
    };
  } else if (testType === TestType.ColorVisionAutomatic) {
    availableCVConfig = {
      ...availableCVConfig,
      virtualAssistantOptions: availableCVConfig.virtualAssistantOptions
        ? {
            ...availableCVConfig.virtualAssistantOptions,
            choices: VIRTUAL_ASSISTANT_LANGUAGE_LIST,
          }
        : null,
    };
  }
  return availableCVConfig;
}
